import React, { useEffect, useMemo, useState, useRef } from 'react'

// Libs
import { useSelector, useDispatch, DefaultRootState } from 'react-redux'
import * as XLSX from 'xlsx'

// Utils
import { safeDivision } from '../../utils/format'
import { getConversionExportValues } from '../../utils/conversion'
import {
  FUTURE_SALES_MESSAGE,
  SALES_DISABLED_FIELD_TOOLTIP_ID,
  getDisabledFieldMessage,
  getDisabledFieldReasons,
  getDisabledFieldTooltipId,
  getReportAvailability,
  getReportExportValue,
  isFieldDisabled
} from '../../utils/reportDisabledFields'

// Material UI
import { makeStyles, useTheme } from '@material-ui/core/styles'
import {
  IconButton,
  Grid,
  Typography,
  useMediaQuery,
  Divider,
  Button,
  Menu,
  MenuItem
} from '@material-ui/core'

import ViewListIcon from '@material-ui/icons/ViewList'
import ViewModuleIcon from '@material-ui/icons/ViewModule'
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown'

// Components
import StoresTable from './components/StoresTable'

// Actions
import { storesReportRequest, lastYearReportRequest } from '../../store/modules/report/actions'
import { listGroupRequest } from 'store/modules/group/actions'
import { clearRedux } from '../../store/modules/main/actions'
import { logout } from 'store/modules/user/actions'

import { GroupsTable } from './components/GroupsTable'
import { GridStoresTable } from './components/GridStoresTable'
import { ExportSpreadSheetModal } from './components/ExportSpreadSheetModal'
import { HeaderCards } from './components/HeaderCards'
import { BulkGoalDeleteDialog } from './components/BulkGoalDeleteDialog'
import { IStoreProps } from 'store/modules/store/types'
import { IGroupFromReport, IGroupProps } from 'store/modules/group/types'
import {
  IReportDisabledFields,
  IReportProps,
  IStoresGroup
} from 'store/modules/report/types'
import { History } from 'history'
import { StoresDatePicker } from './components/StoresDatePicker'
import { IUserProps as IUserReduxProps } from 'store/modules/user/types'
import { StoresSearchBar } from './components/StoreSearchBar'
import { TableContainer } from './styles'
import Tooltip from 'components/Tooltip'

const EMPTY_DISABLED_FIELDS: IReportDisabledFields = {}

const useStyles = makeStyles(theme => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center'
  },
  root: {
    padding: theme.spacing(1),
    margin: theme.spacing(2),
    minWidth: 300
  },

  card: {
    transition: 'all .2s ease-in-out',
    '&:hover': {
      transform: 'scale(1.08)'
    },
    cursor: 'pointer'
  },
  pos: {
    marginBottom: 12
  },
  avatar: {
    justifyContent: 'center',
    width: '100px',
    height: '100px',
    marginBottom: theme.spacing(1)
  },
  sellsContainer: {
    display: 'flex',
    flexDirection: 'column'
  },
  select: {
    width: '150px'
  },
  exportButton: {
    backgroundColor: '#e3e3e8',
    color: '#5b5b5b',
    textTransform: 'none'
  },
  bulkGoalsButton: {
    marginRight: theme.spacing(1)
  },
  menuItemText: {
    width: '100%'
  }
}))

interface StoreProps {
  history: History
}

interface SelectorReport {
  state: DefaultRootState
  report: IReportProps
}

interface SelectorStore {
  state: DefaultRootState
  store: IStoreProps
}

interface SelectorUser {
  state: DefaultRootState
  user: IUserReduxProps
}

interface SelectorGroup {
  state: DefaultRootState
  group: IGroupProps
}

export interface IReports extends IStoresGroup {
  _id: string
  sold: number
  sales: number
  items: number
  goalPercent: number
  acDayGoal: number
  mainGoal: number
  projectionSold: number
  mainGoalPercent: number
}

const Stores = ({ history }: StoreProps) => {
  const dispatch = useDispatch()
  const classes = useStyles()
  const theme = useTheme()
  const bulkGoalsButtonRef = useRef<HTMLButtonElement>(null)
  const exportButtonRef = useRef<HTMLButtonElement>(null)

  // Redux
  const {
    storesReport,
    storesRange,
    lastYearReport
  } = useSelector<SelectorReport, IReportProps>(state => state.report)

  const { store } = useSelector<SelectorStore, IStoreProps>(
    state => state.store
  )
  const { user } = useSelector<SelectorUser, IUserReduxProps>(
    state => state.user
  )
  const {
    groups,
    groupsFromReport,
    hasRequested: hasRequestedGroups,
    listError: groupsError,
    listLoading: groupsLoading
  } = useSelector<SelectorGroup, IGroupProps>(state => state.group)

  const [gridView, setGridView] = useState(false)
  const [reports, setReports] = useState<IReports[]>([])
  const [filteredStores, setFilteredStores] = useState<IReports[]>([])
  const [sheetColumnValue, setSheetColumnValue] = useState<
    (string | number)[][]
  >([])

  const [isSheetModalVisible, setIsSheetModalVisible] = useState(false)
  const [isBulkGoalDeleteOpen, setIsBulkGoalDeleteOpen] = useState(false)
  const [bulkGoalsAnchorEl, setBulkGoalsAnchorEl] = useState<null | HTMLElement>(null)
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)

  const token = window.localStorage.getItem('@NeoPro:token')

  const isDesktop = useMediaQuery(theme.breakpoints.up('sm'), {
    defaultMatches: true
  })

  const disabledFields = storesReport?.disabledFields ?? EMPTY_DISABLED_FIELDS
  const { salesDataReason } = getReportAvailability(disabledFields)
  const hasConversion = reports.some(store => store.conversion)

  const groupsWithData = useMemo(() => {
    if (groups.length > 0 && groupsFromReport.length > 0) {
      const groupsFromReportArray: IGroupFromReport[] = []
      groups.forEach(({ _id }) => {
        const currGroup = groupsFromReport.find(
          ({ groupId }) => groupId === _id
        )

        if (currGroup) {
          const currGroupFromLastYear = lastYearReport?.period?.groups?.find(
            ({ id }) => id === _id
          )

          const storesWithLastYearVariation = currGroup.stores.map(
            storeInGroup => {
              const storeFromLastYear = currGroupFromLastYear?.stores?.find(
                sfly => sfly.id === storeInGroup._id
              )
              return {
                ...storeInGroup,
                lastYearVariation: storeFromLastYear?.variation?.sold
              }
            }
          )

          groupsFromReportArray.push({
            ...currGroup,
            stores: storesWithLastYearVariation,
            lastYearVariation: currGroupFromLastYear?.variation?.sold
          })
        }
      })
      return groupsFromReportArray
    }
    return []
  }, [groups, groupsFromReport, lastYearReport])

  useEffect(() => {
    if (token) {
      dispatch(lastYearReportRequest(token))
    }
  }, [dispatch, token])

  useEffect(() => {
    if (user.stores.length === 1) {
      history.push(`/${user.stores[0].storeId._id}/dashboard`)
    }
  }, [user.stores, user.stores.length, history])

  useEffect(() => {
    if (!hasRequestedGroups && token) {
      dispatch(listGroupRequest(token))
    }
  }, [hasRequestedGroups, token, dispatch])

  useEffect(() => {
    let stores: IStoresGroup[] = []
    if (storesReport) {
      if (storesReport.stores.length) {
        stores = storesReport.stores
      }
      if (storesReport.groups.length) {
        const groupsStores = storesReport.groups
          // pega todas as lojas de cada regional
          .map(group => group.stores)
          // deixa em um único array
          .flat(1)
          // remove as lojas duplicadas
          .filter(
            (store, index, self) =>
              index === self.findIndex(att => att._id === store._id)
          )

        stores = [...stores, ...groupsStores]
      }

      const lastYearVariationsMap = new Map<string, number>()
      if (lastYearReport?.period?.groups) {
        for (const groupLY of lastYearReport.period.groups) {
          groupLY.stores?.forEach(storeLY => {
            if (storeLY.id && storeLY.variation?.sold !== undefined) {
              lastYearVariationsMap.set(storeLY.id, storeLY.variation.sold)
            }
          })
        }
      }

      setReports(
        stores.map(store => {
          const variation = lastYearVariationsMap.get(store._id)
          return {
            ...store,
            lastYearVariation: variation,
            sold: store.total.sold,
            sales: store.total.sales,
            items: Number(store.total.items.toFixed()),
            goalPercent: isFieldDisabled(salesDataReason) || isFieldDisabled(
              disabledFields.metric?.acDayGoal
            )
              ? 0
              : Number(
                (safeDivision(store.total.sold, store.metric.acDayGoal) * 100)
                  .toFixed(2)
              ),
            acDayGoal: store.metric.acDayGoal,
            mainGoal: store.metric.mainGoal,
            projectionSold: store.projection.sold,
            mainGoalPercent: isFieldDisabled(salesDataReason) || isFieldDisabled(
              disabledFields.metric?.mainGoal
            )
              ? 0
              : Number(
                (safeDivision(store.total.sold, store.metric.mainGoal) * 100)
                  .toFixed(2)
              )
          }
        })
      )
    }
  }, [disabledFields, salesDataReason, storesReport, lastYearReport])

  useEffect(() => {
    const tableHeaders = [
      'Nome',
      // 'Cidade',
      'Vendas do dia',
      'Meta do dia',
      'Faturamento',
      '% da Meta',
      'Meta ideal',
      '% da Meta do mês',
      'Meta do mês',
      'Projeção de Venda',
      'Nº Vendas',
      'Ticket Médio',
      'Peça/Venda',
      'Preço Médio',
      'Nº Peças',
      ...(hasConversion ? ['Pessoas', 'Conversão'] : [])
    ]

    setSheetColumnValue([tableHeaders])

    reports?.forEach(store => {
      const tableData = [
        store.name,
        // store.city,
        getReportExportValue(salesDataReason, store.daySold.sold ?? 0),
        store.dayGoals.sell ?? 0,
        getReportExportValue(salesDataReason, store.total.sold ?? 0),
        isFieldDisabled(salesDataReason) ||
        isFieldDisabled(disabledFields.metric?.acDayGoal)
          ? '-'
          : safeDivision(store.total.sold, store.metric.acDayGoal), // % da Meta
        getReportExportValue(
          disabledFields.metric?.acDayGoal,
          store.metric.acDayGoal ?? 0
        ), // Meta ideal
        isFieldDisabled(salesDataReason) ||
        isFieldDisabled(disabledFields.metric?.mainGoal)
          ? '-'
          : safeDivision(store.total.sold, store.metric.mainGoal), // % da Meta do mês
        getReportExportValue(
          disabledFields.metric?.mainGoal,
          store.metric.mainGoal ?? 0
        ), // Meta do mês
        getReportExportValue(
          disabledFields.projection?.sold,
          store.projectionSold ?? 0
        ), // Projeção de Venda
        getReportExportValue(salesDataReason, store.total.sales ?? 0),
        getReportExportValue(salesDataReason, store.average.tm ?? 0),
        getReportExportValue(salesDataReason, store.average.pv ?? 0),
        getReportExportValue(salesDataReason, store.average.pm ?? 0),
        getReportExportValue(salesDataReason, store.total.items ?? 0),
        ...(hasConversion
          ? getConversionExportValues(store.conversion, salesDataReason)
          : [])
      ]

      setSheetColumnValue(state => [...state, tableData])
    })
  }, [disabledFields, hasConversion, reports, salesDataReason])

  const handleOnExport = () => {
    const wb = XLSX.utils.book_new()
    const from = storesRange.from.toLocaleDateString('pt-BR')
    const to = storesRange.to.toLocaleDateString('pt-BR')
    const ws = XLSX.utils.aoa_to_sheet([
      [`Período: ${from} até ${to}`],
      [],
      ...sheetColumnValue
    ])

    new Array(reports.length + 1).fill(0).forEach((_, index) => {
      const row = index + 3
      ws[`B${row}`].z = '"R$ "#,##0.00' // Vendas do dia
      ws[`C${row}`].z = '"R$ "#,##0.00' // Meta do dia
      ws[`D${row}`].z = '"R$ "#,##0.00' // Faturamento
      ws[`E${row}`].z = '0.00%' // % da Meta
      ws[`F${row}`].z = '"R$ "#,##0.00' // Meta ideal
      ws[`G${row}`].z = '0.00%' // % da Meta do mês
      ws[`H${row}`].z = '"R$ "#,##0.00' // Meta do mês
      ws[`I${row}`].z = '"R$ "#,##0.00' // Projeção de venda
      ws[`J${row}`].z = '0' // Nº de vendas
      ws[`K${row}`].z = '"R$ "#,##0.00' // Ticket médio
      ws[`L${row}`].z = '0.00' // Peça/Venda
      ws[`M${row}`].z = '"R$ "#,##0.00' // Preço médio
      ws[`N${row}`].z = '0' // Nº de Peças
      if (hasConversion) {
        ws[`O${row}`].z = '0' // Pessoas
        ws[`P${row}`].z = '0.00%' // Conversão
      }
    })

    XLSX.utils.book_append_sheet(wb, ws, `Relatório`)
    XLSX.writeFile(wb, `NEOPRO - Relatorio-Lojas-${from.replace(/\//g, '-')}_${to.replace(/\//g, '-')}.xlsx`, {
      bookType: 'xlsx',
      type: 'binary'
    })
  }

  const handleExportMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleExportMenuClose = () => {
    setAnchorEl(null)
  }

  const handleExportOptionClick = (option: 'summary' | 'allStores') => {
    if (option === 'summary') {
      handleOnExport()
    } else if (option === 'allStores') {
      setIsSheetModalVisible(true)
    }
    handleExportMenuClose()
  }

  useEffect(() => {
    if (token) {
      if (!storesReport && user._id) {
        const isDirector = user.stores.every(
          storeUser => storeUser.type === 'communicator'
        )

        if (!isDirector) {
          dispatch(storesReportRequest(user._id, token))
        } else {
          history.push('/storylines')
        }
      }
    } else {
      window.localStorage.removeItem('@NeoPro:token')
      dispatch(logout())
      history.push(`/login`)
    }
  }, [token, user, storesReport, dispatch, history])

  const handleSelectStore = (storeId: string) => {
    history.push(`/${storeId}/dashboard`)
  }

  const handleBulkGoalsMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setBulkGoalsAnchorEl(event.currentTarget)
  }

  const handleBulkGoalsMenuClose = () => {
    setBulkGoalsAnchorEl(null)
  }

  const handleOpenBulkGoals = () => {
    history.push('/stores/goals/bulk-create')
    handleBulkGoalsMenuClose()
  }

  const handleOpenBulkGoalDelete = () => {
    setIsBulkGoalDeleteOpen(true)
    handleBulkGoalsMenuClose()
  }

  const handleBulkGoalsDeleted = () => {
    if (token && user._id) {
      dispatch(storesReportRequest(user._id, token, storesRange))
    }
  }

  useEffect(() => {
    if (store) dispatch(clearRedux()) // se tiver store quer dizer que ele ja entrou numa store e voltou sem ser no botao de trocar de loja
  }, [store, dispatch])

  const hasGroups = groupsWithData.length > 0

  const hasFilteredStores = filteredStores.length > 0
  const hasStoreWithoutInputs = storesReport?.stores.some(
    store => store.hasInputs === false
  ) || storesReport?.groups.some(group =>
    group.hasInputs === false || group.stores.some(store => store.hasInputs === false)
  )
  const disabledFieldReasons = getDisabledFieldReasons(disabledFields)

  return (
    <div className={classes.root}>
      {disabledFieldReasons.map(reason => (
        <Tooltip
          key={reason}
          id={getDisabledFieldTooltipId(reason)}
          content={<p>{getDisabledFieldMessage(reason)}</p>}
          place='bottom'
        />
      ))}
      {hasStoreWithoutInputs && !disabledFieldReasons.includes('NO_SALES_RECORDS') && (
        <Tooltip
          id={getDisabledFieldTooltipId('NO_SALES_RECORDS')}
          content={<p>{getDisabledFieldMessage('NO_SALES_RECORDS')}</p>}
          place='bottom'
        />
      )}
      {salesDataReason && (
        <Tooltip
          id={SALES_DISABLED_FIELD_TOOLTIP_ID}
          content={<p>{FUTURE_SALES_MESSAGE}</p>}
          place='bottom'
        />
      )}
      <Typography variant='h4' style={{ marginBottom: 8 }}>
        Todas as lojas
      </Typography>

      <Grid container spacing={4}>
        <HeaderCards
          disabledFields={disabledFields}
          storesReport={storesReport}
          lastYearReport={lastYearReport}
        />

        <Grid item xs={12}>
          <Divider
            style={{
              height: 1,
              backgroundColor: theme.palette.action.disabled,
              opacity: 0.3
            }}
          />
        </Grid>

        <Grid container style={{ paddingRight: 16 }}>
          <Grid item xs={6} style={{ marginBottom: 16 }}>
            {storesReport && !(storesReport.groups.length > 0) && (
              <IconButton
                onClick={() => setGridView(!gridView)}
                color='primary'
                aria-label='upload picture'
                component='span'
              >
                {gridView ? <ViewListIcon /> : <ViewModuleIcon />}
              </IconButton>
            )}
          </Grid>

          {!gridView && (
            <Grid
              item
              xs={12}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                flexDirection: isDesktop ? 'row' : 'column'
              }}
            >
              <Grid
                item
                xs={isDesktop ? 6 : 10}
                style={{ display: 'flex', paddingLeft: 16 }}
              >
                <StoresSearchBar
                  stores={reports}
                  setFilteredStores={setFilteredStores}
                />
                <StoresDatePicker range={storesRange} />
              </Grid>

              <Grid
                item
                xs={isDesktop ? 6 : 12}
                style={{
                  display: 'flex',
                  justifyContent: isDesktop ? 'flex-end' : 'center',
                  paddingTop: isDesktop ? 0 : theme.spacing(2)
                }}
              >
                <Button
                  ref={bulkGoalsButtonRef}
                  className={`${classes.exportButton} ${classes.bulkGoalsButton}`}
                  aria-controls={bulkGoalsAnchorEl ? 'bulk-goals-menu' : undefined}
                  aria-expanded={Boolean(bulkGoalsAnchorEl)}
                  aria-haspopup="true"
                  onClick={handleBulkGoalsMenuClick}
                  variant='contained'
                  endIcon={<ArrowDropDownIcon />}
                  disableElevation
                >
                  MÚLTIPLAS METAS
                </Button>
                <Menu
                  id="bulk-goals-menu"
                  anchorEl={bulkGoalsAnchorEl}
                  keepMounted
                  open={Boolean(bulkGoalsAnchorEl)}
                  onClose={handleBulkGoalsMenuClose}
                  getContentAnchorEl={null}
                  anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                  }}
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                  }}
                  PaperProps={{
                    style: {
                      width: bulkGoalsAnchorEl ? bulkGoalsButtonRef.current?.offsetWidth : undefined,
                    },
                  }}
                >
                  <MenuItem onClick={handleOpenBulkGoals}>
                    <Typography className={classes.menuItemText}>Cadastrar</Typography>
                  </MenuItem>
                  <MenuItem onClick={handleOpenBulkGoalDelete}>
                    <Typography className={classes.menuItemText}>Excluir</Typography>
                  </MenuItem>
                </Menu>
                <Button
                  ref={exportButtonRef}
                  aria-controls="export-menu"
                  aria-haspopup="true"
                  onClick={handleExportMenuClick}
                  variant="contained"
                  className={classes.exportButton}
                  endIcon={<ArrowDropDownIcon />}
                  disableElevation
                >
                  EXPORTAR RELATÓRIO
                </Button>
                <Menu
                  id="export-menu"
                  anchorEl={anchorEl}
                  keepMounted
                  open={Boolean(anchorEl)}
                  onClose={handleExportMenuClose}
                  getContentAnchorEl={null}
                  anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                  }}
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                  }}
                  PaperProps={{
                    style: {
                      width: anchorEl ? exportButtonRef.current?.offsetWidth : undefined,
                    },
                  }}
                >
                  <MenuItem onClick={() => handleExportOptionClick('summary')}>
                    <Typography className={classes.menuItemText}>Resumo da tela</Typography>
                  </MenuItem>
                  <MenuItem onClick={() => handleExportOptionClick('allStores')}>
                    <Typography className={classes.menuItemText}>Mês de todas lojas</Typography>
                  </MenuItem>
                </Menu>
              </Grid>
            </Grid>
          )}
        </Grid>

        <Grid item xs={12}>
          {hasGroups ? (
            <>
              <TableContainer isVisible={hasFilteredStores}>
                <StoresTable
                  storesReport={filteredStores}
                  referenceConversion={storesReport?.storesInfos.conversion}
                  handleSelectStore={handleSelectStore}
                  disabledFields={disabledFields}
                />
              </TableContainer>
              <TableContainer isVisible={!hasFilteredStores}>
                <Grid container>
                  <GroupsTable
                    groups={groupsWithData}
                    disabledFields={disabledFields}
                  />
                </Grid>

                {storesReport && storesReport?.stores.length > 0 && (
                  <>
                    <Typography
                      variant='h4'
                      style={{
                        marginBottom: 16,
                        marginTop: 32,
                        marginLeft: 8
                      }}
                    >
                      Lojas sem regional
                    </Typography>

                    <StoresTable
                      storesReport={storesReport}
                      handleSelectStore={handleSelectStore}
                      disabledFields={disabledFields}
                    />
                  </>
                )}
              </TableContainer>
            </>
          ) : gridView ? (
            <Grid container>
              {storesReport &&
                storesReport.stores.map(store => (
                  <GridStoresTable
                    key={store._id}
                    store={store}
                    handleSelectStore={handleSelectStore}
                    disabledFields={disabledFields}
                  />
                ))}
            </Grid>
          ) : (
            !!storesReport && (
              <>
                <TableContainer isVisible={hasFilteredStores}>
                  <StoresTable
                    storesReport={filteredStores}
                    referenceConversion={storesReport?.storesInfos.conversion}
                    handleSelectStore={handleSelectStore}
                    disabledFields={disabledFields}
                  />
                </TableContainer>

                <TableContainer isVisible={!hasFilteredStores}>
                  <StoresTable
                    storesReport={storesReport}
                    handleSelectStore={handleSelectStore}
                    disabledFields={disabledFields}
                  />
                </TableContainer>
              </>
            )
          )}
        </Grid>
      </Grid>

      {reports && (
        <ExportSpreadSheetModal
          isOpen={isSheetModalVisible}
          onClose={() => setIsSheetModalVisible(false)}
        />
      )}
      <BulkGoalDeleteDialog
        groups={groups}
        groupsError={Boolean(groupsError)}
        groupsLoading={groupsLoading}
        open={isBulkGoalDeleteOpen}
        onClose={() => setIsBulkGoalDeleteOpen(false)}
        onDeleted={handleBulkGoalsDeleted}
        onReloadGroups={() => dispatch(listGroupRequest(token))}
      />
    </div>
  )
}

export default Stores
